import { anomaly } from '../diagnostics';
import { EventBus, GameEventTypes } from '../EventBus';
import { Canonical } from '../hashState';
import { Portal } from '@antification/protocol';
import { Rng, RngState } from '../Rng';
import { fromJsonNumber, jsonNumber } from '../snapshot';
import { AntExportData, AntTask, cloneAntExport } from '../colony/Ant';
import { CarManager } from './CarManager';
import { FoodSourceManager } from './FoodSourceManager';
import { HouseManager } from './HouseManager';
import { Direction8, DIR_VECTORS, PheromoneGrid, turnDirection } from './PheromoneGrid';
import { CellState, TerrainGrid } from './TerrainGrid';

/**
 * Surface foraging config type (per-colony tunable).
 */
export interface SurfaceForagingConfig {
  SURFACE_SPEED_MIN: number;
  SURFACE_SPEED_RANGE: number;
  SURFACE_TIME_MIN: number;
  SURFACE_TIME_RANGE: number;
  MOVE_DECISION_MIN: number;
  MOVE_DECISION_RANGE: number;
  FOOD_MULTIPLIER: number;
  EXPOSURE_MULTIPLIER: number;
}

export const DEFAULT_SURFACE_FORAGING: SurfaceForagingConfig = {
  SURFACE_SPEED_MIN: 300,
  SURFACE_SPEED_RANGE: 200,
  SURFACE_TIME_MIN: 15000,
  SURFACE_TIME_RANGE: 15000,
  MOVE_DECISION_MIN: 100,
  MOVE_DECISION_RANGE: 200,
  FOOD_MULTIPLIER: 1.0,
  EXPOSURE_MULTIPLIER: 3.0,
};

/**
 * Surface ant behavior mode for biologically plausible foraging.
 */
type AntBehaviorMode = 'search' | 'follow_trail' | 'wall_follow' | 'local_search';

/**
 * Surface ant that has emerged from the colony.
 * Moves in grid space and can be killed by cars.
 * Implements biologically plausible trail-following behavior.
 */
export interface SurfaceAnt {
  id: number;
  name: string;
  gridX: number;
  gridY: number;
  targetGridX: number;
  targetGridY: number;
  /**
   * How far between `gridX,gridY` and `targetGridX,targetGridY` this ant is, 0–1.
   *
   * The only thing that describes where an ant is *between* cells. It used to be
   * accompanied by a `screenX`/`screenY` pair the simulation recomputed for
   * every ant every tick, from a camera it had no other use for; the renderer
   * interpolates the same two points itself now (ADR-009).
   */
  progress: number;
  speed: number;
  size: number;
  age: number;
  maxAge: number;
  state: 'foraging' | 'returning' | 'dead';
  entryPortalId: string;
  moveTimer: number; // Time until next move attempt
  // Foraging state
  hasFood: boolean;
  carriedFoodAmount: number;
  carriedFoodHue: number; // Color hue of carried food (0-40, red to yellow)
  targetFoodX: number | null;
  targetFoodY: number | null;
  // Original colony data for return transfer
  originalData: AntExportData;
  // Pathfinding state - to escape local minima
  stuckCounter: number;
  lastDistanceToTarget: number;
  recentCells: string[]; // Track recent positions to avoid loops
  // Colony tracking
  colonyIndex: number;

  // === Biologically plausible additions ===
  // 8-way heading direction
  heading: Direction8;
  // Behavior mode (overlay on state)
  behaviorMode: AntBehaviorMode;
  // Trail confidence - short-term memory of pheromone strength (0-1)
  trailConfidence: number;
  // Consecutive ticks sensing trail above threshold
  trailSenseCounter: number;
  // Wall-following state
  wallFollowSide: 'left' | 'right' | 'none';
  wallFollowTimer: number; // Ticks remaining in wall-follow mode
  // Pheromone deposit timer (to avoid depositing every tick)
  pheromoneDepositTimer: number;
  // Gland capacity for pheromone deposition (0-1, depletes as ant deposits)
  gland: number;
  /**
   * Sim-time in ms this ant has left lying where a car hit it, or null while alive.
   *
   * A corpse is visible for a moment before it is removed. That used to be a
   * `setTimeout`, which is both I/O in a simulation path and a promise the
   * simulation cannot keep: a headless run has no event loop to fire it, and a
   * paused game would clear the body anyway.
   */
  corpseTimer: number | null;
}

/** How long a dead ant stays on the pavement, in sim-milliseconds. */
export const CORPSE_LINGER_MS = 2000;

/**
 * A surfaced ant as it appears in a snapshot.
 *
 * Identical to the live ant but for one field. `lastDistanceToTarget` starts at
 * `Infinity`, meaning *no distance measured yet*, and JSON has no infinities —
 * so it is stored as `null` and read back as `Infinity`. Assigning the `null`
 * straight through would give the stuck-detector a value that compares as zero,
 * which is the kind of bug that shows up three thousand ticks later as an ant
 * that will not admit it is lost.
 */
export interface SurfaceAntSnapshot extends Omit<SurfaceAnt, 'lastDistanceToTarget'> {
  lastDistanceToTarget: number | null;
}

/** Every ant above ground. See `ParkingtonSurfaceAntController.save()`. */
export interface SurfaceAntsSnapshot {
  ants: SurfaceAntSnapshot[];
  rng: RngState;
}

/** Deep copies, so a snapshot cannot be edited by a later tick. */
function toAntSnapshot(ant: SurfaceAnt): SurfaceAntSnapshot {
  return {
    ...ant,
    lastDistanceToTarget: jsonNumber(ant.lastDistanceToTarget),
    recentCells: [...ant.recentCells],
    originalData: cloneAntExport(ant.originalData),
  };
}

function fromAntSnapshot(saved: SurfaceAntSnapshot): SurfaceAnt {
  return {
    ...saved,
    lastDistanceToTarget: fromJsonNumber(saved.lastDistanceToTarget, Infinity),
    recentCells: [...saved.recentCells],
    originalData: cloneAntExport(saved.originalData),
  };
}

export interface BlendWeights {
  noiseWeight: number;
  foodOdorWeight: number;
  trailWeight: number;
  nestWeight: number;
  directWeight: number;
  edgeWeight: number;
  alarmWeight: number;
}

export const DEFAULT_BLEND_WEIGHTS: BlendWeights = {
  noiseWeight: 0.2,
  foodOdorWeight: 0.3,
  trailWeight: 0.5,
  nestWeight: 0.3,
  directWeight: 0.4,
  edgeWeight: 0.05,
  alarmWeight: 0.3,
};

interface SurfaceAntDeps {
  grid: TerrainGrid;
  cars: CarManager;
  houses: HouseManager;
  foodSources: FoodSourceManager;
  pheromones: PheromoneGrid;
  portals: Portal[];
  portalCells: Array<{ x: number; y: number }>;
  events: EventBus;
  layerId: string;
  getSurfaceForagingConfig: (colonyIndex: number) => SurfaceForagingConfig;
  rng: Rng;
  /** Sim-time reader, in milliseconds. Stamps outgoing transfers. */
  now: () => number;
  /**
   * Whether a cell has a way under the road on it.
   *
   * §10's wildlife undercrossing: *"Ants path beneath a road segment; cars can't
   * kill what crosses there."* One predicate, and it is the entire mechanic — the
   * ant walks the same route at the same speed and the collision simply is not one.
   *
   * Injected rather than reached for, because the thing that knows is
   * `MercyManager`, and `MercyManager` must never be able to find out that this
   * question was ever asked. Law 7 in the direction the design does not spell out:
   * a mayor pays for a crossing and the game will never tell them it worked.
   *
   * Omitted means nothing is sheltered anywhere, which is Parkington before ADR-020
   * and is what keeps the corpus reading the same.
   */
  isSheltered?: (x: number, y: number) => boolean;
  pheromoneDepositInterval: number;
  trailFollowThreshold: number;
  wallFollowDuration: number;
  glandCapacity: number;
  glandCostPerDeposit: number;
  blendWeights: BlendWeights;
}

/**
 * The subset of the deps that trait application is allowed to rewrite at
 * runtime, exposed by reference.
 *
 * `ColonyTraits` recomputes these when the player moves a slider. This used to
 * be done by casting the controller to `any` and reaching for its private deps;
 * naming the mutable surface is the same thing said out loud.
 */
export interface SurfaceAntTunables {
  pheromoneDepositInterval: number;
  wallFollowDuration: number;
  glandCapacity: number;
  glandCostPerDeposit: number;
}

export class ParkingtonSurfaceAntController {
  private surfaceAnts: Map<number, SurfaceAnt> = new Map();

  constructor(private deps: SurfaceAntDeps) {}

  /** The trait-mutable tunables, by reference. */
  get tunables(): SurfaceAntTunables {
    return this.deps;
  }

  getAnts(): IterableIterator<SurfaceAnt> {
    return this.surfaceAnts.values();
  }

  getAntCount(): number {
    return this.surfaceAnts.size;
  }

  getAntCountForColony(colonyIndex: number): number {
    let count = 0;
    for (const ant of this.surfaceAnts.values()) {
      if (ant.colonyIndex === colonyIndex && ant.state !== 'dead') {
        count++;
      }
    }
    return count;
  }

  clearAntsForColony(colonyIndex: number): void {
    const toRemove: number[] = [];
    for (const [id, ant] of this.surfaceAnts) {
      if (ant.colonyIndex === colonyIndex) {
        toRemove.push(id);
      }
    }
    for (const id of toRemove) {
      this.surfaceAnts.delete(id);
    }
  }

  clearAllAnts(): void {
    this.surfaceAnts.clear();
  }

  /**
   * Every ant above ground, including the dead ones.
   *
   * The client's old save format dropped the dead, which was defensible for a
   * record a player would look at and wrong for a snapshot: a corpse has a
   * `corpseTimer` counting down, and a body that vanished on load would be a body
   * the ledger below never got to see removed.
   *
   * **In arrival order, not sorted.** This is the one place where the snapshot's
   * rule and the hash's rule point in opposite directions, and the difference is
   * load-bearing. `canonicalState()` sorts by id so that two runs which decided
   * the same things agree; here, the order *is* a decision. An ant's id is its
   * colony ant id, so three colonies' ants interleave as they surface, and
   * `update()` draws one value per ant per tick from a shared stream. Sorting
   * would restore a world that agreed on every ant and then dealt them each
   * other's dice.
   */
  save(): SurfaceAntsSnapshot {
    return {
      ants: Array.from(this.surfaceAnts.values()).map(toAntSnapshot),
      rng: this.deps.rng.save(),
    };
  }

  /** Restore from `save()`. */
  load(state: SurfaceAntsSnapshot): void {
    this.surfaceAnts.clear();
    for (const saved of state.ants) {
      const ant = fromAntSnapshot(saved);
      this.surfaceAnts.set(ant.id, ant);
    }
    this.deps.rng.load(state.rng);
  }

  /**
   * Order-stable view of the ants above ground, for hashing.
   *
   * Sorted by id. This used to exclude `screenX`/`screenY` by hand, with a note
   * saying an ant's fate must not depend on where the mayor is looking; the
   * fields are gone (ADR-009), so the omission is no longer something the next
   * person to add a field has to remember.
   */
  canonicalState(): Canonical {
    return Array.from(this.surfaceAnts.values())
      .sort((a, b) => a.id - b.id)
      .map(a => ({
        id: a.id,
        name: a.name,
        x: a.gridX,
        y: a.gridY,
        tx: a.targetGridX,
        ty: a.targetGridY,
        progress: a.progress,
        speed: a.speed,
        age: a.age,
        maxAge: a.maxAge,
        state: a.state,
        colony: a.colonyIndex,
        heading: a.heading,
        behaviorMode: a.behaviorMode,
        hasFood: a.hasFood,
        carriedFoodAmount: a.carriedFoodAmount,
        carriedFoodHue: a.carriedFoodHue,
        moveTimer: a.moveTimer,
        trailConfidence: a.trailConfidence,
        trailSenseCounter: a.trailSenseCounter,
        wallFollowSide: a.wallFollowSide,
        wallFollowTimer: a.wallFollowTimer,
        pheromoneDepositTimer: a.pheromoneDepositTimer,
        gland: a.gland,
        stuckCounter: a.stuckCounter,
        corpseTimer: a.corpseTimer,
      }));
  }

  /**
   * Put a surfacing ant on the ground, or refuse it.
   *
   * Returns whether the surface took the ant — the layer-transfer contract's one
   * new obligation, and the reason it needed one. Everything above this call is a
   * colony: `ColonySim.tryExitToSurface` builds the entity, emits it, and then
   * asks whether the ant is really up there, so a `false` here leaves the forager
   * below ground with its task reset and nothing to show for the attempt. A colony
   * whose foragers keep walking to a hole that is not there is exactly what a
   * colony under a foundation would look like from the inside, and it is not told
   * anything else.
   *
   * The refusal comes **before** the heading is drawn, which is the load-bearing
   * half: an ant that never surfaced must not consume a value from the surface's
   * generator, or a district's whole ant stream would depend on how many times
   * somebody knocked.
   */
  handleAntArrival(antData: AntExportData, portalId: string): boolean {
    const portal = this.deps.portals.find(p => p.id === portalId);
    if (!portal) {
      anomaly(`[ANT_ARRIVAL] Portal not found: ${portalId}, ant: ${antData.id}`);
      return false;
    }

    // Extract colony index from portal ID (format: surface-entrance-colony-X-Y)
    const colonyMatch = portalId.match(/colony-(\d+)/);
    const colonyIndex = colonyMatch ? parseInt(colonyMatch[1], 10) : 0;

    // Get the portal cell for this colony
    const portalCell = this.getPortalCell(colonyIndex);
    if (!portalCell) {
      anomaly(`[ANT_ARRIVAL] Portal cell not found for colony ${colonyIndex}, ant: ${antData.id}`);
      return false;
    }

    // Concrete over the hole. Not an anomaly and not an error: a slab was poured
    // here and the way out is gone.
    if (this.entranceIsSealed(colonyIndex)) return false;

    // No longer assign global food target - ants use local food odor sensing

    // Pick random initial heading (biased away from edges)
    const initialHeading = this.deps.rng.int(8) as Direction8;
    const config = this.deps.getSurfaceForagingConfig(colonyIndex);

    const surfaceAnt: SurfaceAnt = {
      id: antData.id,
      name: antData.name,
      gridX: portalCell.x,
      gridY: portalCell.y,
      targetGridX: portalCell.x,
      targetGridY: portalCell.y,
      progress: 1,
      speed: config.SURFACE_SPEED_MIN + this.deps.rng.next() * config.SURFACE_SPEED_RANGE,
      size: antData.size * 4 + 2, // Scale up for visibility (6-10 pixels)
      age: 0,
      maxAge: config.SURFACE_TIME_MIN + this.deps.rng.next() * config.SURFACE_TIME_RANGE,
      state: 'foraging',
      entryPortalId: portalId,
      moveTimer: 0,
      hasFood: false,
      carriedFoodAmount: 0,
      carriedFoodHue: 0,
      targetFoodX: null,
      targetFoodY: null,
      originalData: antData,
      stuckCounter: 0,
      lastDistanceToTarget: Infinity,
      recentCells: [],
      colonyIndex,
      heading: initialHeading,
      behaviorMode: 'search',
      trailConfidence: 0,
      trailSenseCounter: 0,
      wallFollowSide: 'none',
      wallFollowTimer: 0,
      pheromoneDepositTimer: 0,
      gland: this.deps.glandCapacity,
      corpseTimer: null,
    };

    this.surfaceAnts.set(antData.id, surfaceAnt);

    this.deps.events.emit(GameEventTypes.ANT_ENTERED, {
      antId: antData.id,
      name: antData.name,
      portalId,
      colonyIndex,
    }, this.deps.layerId);

    return true;
  }

  update(dt: number): void {
    for (const [id, ant] of this.surfaceAnts) {
      if (ant.state === 'dead') {
        // Let the body linger, then remove it. Deleting from a Map while
        // iterating it is defined behaviour in JavaScript and the entry is
        // already behind us. A dead ant with no timer — only reachable from an
        // old save — goes immediately rather than lying there forever.
        if (ant.corpseTimer === null) {
          this.surfaceAnts.delete(id);
          continue;
        }
        ant.corpseTimer -= dt;
        if (ant.corpseTimer <= 0) this.surfaceAnts.delete(id);
        continue;
      }

      ant.age += dt;

      if (this.isRunOver(ant.gridX, ant.gridY)) {
        this.killSurfaceAnt(ant, 'car collision');
        continue;
      }

      if (ant.state === 'foraging' && (ant.hasFood || ant.age >= ant.maxAge)) {
        ant.state = 'returning';
        ant.behaviorMode = 'search';
      }

      if (ant.state === 'returning' && ant.hasFood) {
        ant.pheromoneDepositTimer += dt;
        if (ant.pheromoneDepositTimer >= this.deps.pheromoneDepositInterval) {
          if (ant.gland > 0.05) {
            const baseAmount = this.deps.pheromones.TRAIL_DEPOSIT_AMOUNT
              * (0.5 + 0.5 * Math.min(1, ant.carriedFoodAmount / 10));
            const actualDeposit = baseAmount * ant.gland;
            this.deps.pheromones.depositTrail(ant.gridX, ant.gridY, actualDeposit);
            ant.gland = Math.max(0, ant.gland - this.deps.glandCostPerDeposit);
          }
          ant.pheromoneDepositTimer = 0;
        }
      }

      if (ant.age >= ant.maxAge * this.deps.getSurfaceForagingConfig(ant.colonyIndex).EXPOSURE_MULTIPLIER) {
        // Exposure, not traffic. This line has reported `'car collision'` since the
        // deaths were first recorded, because `killSurfaceAnt` had one cause in it
        // and two callers — so every ant that ever died of the sun on this surface
        // was booked as having been run over.
        //
        // Found by ADR-020, and found because the undercrossing is the first mechanic
        // whose whole claim is about *which* deaths it prevents: §10 says cars cannot
        // kill what crosses there, and a test of that sentence needs the two causes
        // to be different words. The cause reaches no state — the colony's own line
        // is *"was lost on the surface"* either way — so nothing hashed moved.
        //
        // The third word is the seal's (Q27). An ant of a colony whose entrance is
        // under a slab did not die of being lost: there was nowhere to arrive, for
        // it or for any of its sisters, from the tick the concrete went down. The
        // sun is still what kills it and the clock is still the same clock — what
        // the cause names is the reason it was never going to beat that clock.
        this.killSurfaceAnt(ant, this.entranceIsSealed(ant.colonyIndex) ? 'sealed out' : 'exposure');
        continue;
      }

      if (ant.progress < 1) {
        ant.progress += dt / ant.speed;
        if (ant.progress >= 1) {
          ant.progress = 1;
          ant.gridX = ant.targetGridX;
          ant.gridY = ant.targetGridY;

          if (this.isRunOver(ant.gridX, ant.gridY)) {
            this.killSurfaceAnt(ant, 'car collision');
            continue;
          }

          if (ant.state === 'foraging' && !ant.hasFood) {
            const collected = this.deps.foodSources.collectFood(ant.gridX, ant.gridY);
            if (collected.amount > 0) {
              ant.hasFood = true;
              ant.carriedFoodAmount = collected.amount
                * this.deps.getSurfaceForagingConfig(ant.colonyIndex).FOOD_MULTIPLIER;
              ant.carriedFoodHue = collected.hue;
              ant.state = 'returning';
              ant.behaviorMode = 'search';
            }
            // No global food re-query; ants rely on food odor gradient
          }
        }
      } else {
        ant.moveTimer += dt;

        const antPortalCell = this.getPortalCell(ant.colonyIndex);
        if (ant.state === 'returning'
          && antPortalCell
          && ant.gridX === antPortalCell.x
          && ant.gridY === antPortalCell.y
          // The other half of the seal, and the half with a body at the end of
          // it. An ant that walked out through a hole and came back to a slab is
          // standing on the right cell and there is nothing to go down. It is not
          // stopped, killed or redirected here — it keeps its `returning` state
          // and keeps walking, which is what the run of the loop below does with
          // it, until the exposure clock runs out.
          && !this.entranceIsSealed(ant.colonyIndex)) {
          this.returnAntToColony(ant);
          this.surfaceAnts.delete(id);
          continue;
        }

        // The interval is drawn every tick whether or not it fires, so the
        // number of values an ant consumes per tick is fixed. Drawing only when
        // the timer is close would make an ant's stream depend on its own
        // history in a way that is very hard to reason about later.
        const moveConfig = this.deps.getSurfaceForagingConfig(ant.colonyIndex);
        const decisionInterval = moveConfig.MOVE_DECISION_MIN
          + this.deps.rng.next() * moveConfig.MOVE_DECISION_RANGE;
        if (ant.moveTimer >= decisionInterval) {
          ant.moveTimer = 0;
          this.tryMoveAnt(ant);
        }
      }
    }
  }

  private returnAntToColony(ant: SurfaceAnt): void {
    const portal = this.deps.portals.find(p => p.id === ant.entryPortalId);
    if (!portal) {
      anomaly(`[ANT_RETURN] Portal not found for ant ${ant.id}, portalId: ${ant.entryPortalId}`);
      // Emit death event so the formicarium can clean up its antsOnSurface tracking
      this.deps.events.emit(GameEventTypes.ANT_KILLED_ON_SURFACE, {
        antId: ant.id,
        name: ant.name,
        gridX: ant.gridX,
        gridY: ant.gridY,
        cause: 'lost (portal missing)',
        colonyIndex: ant.colonyIndex,
      }, this.deps.layerId, `formicarium-${ant.colonyIndex}`);
      return;
    }

    const returnData: AntExportData = {
      ...ant.originalData,
      carryingFood: ant.hasFood,
      carriedFoodAmount: ant.carriedFoodAmount,
      carriedFoodHue: ant.carriedFoodHue,
      task: ant.hasFood ? AntTask.CARRYING_FOOD : AntTask.NONE,
    };

    const entity = {
      type: 'ant',
      data: returnData,
      sourcePortal: ant.entryPortalId,
      sourceLayer: this.deps.layerId,
      timestamp: this.deps.now(),
    };

    this.deps.events.emit('ANT_TRANSFER_REQUEST', {
      entity,
      targetLayer: portal.targetLayerId,
      targetPortal: portal.targetPortalId,
    }, this.deps.layerId);
  }

  /**
   * Whether an ant standing here is under a car's wheels.
   *
   * The shelter check comes first, and not only because a set lookup is cheaper
   * than a walk of every moving car: it is the honest order. §10 says an ant on a
   * cell with an undercrossing *"paths beneath"* the road, so it was never in the
   * traffic to begin with. The collision does not happen and is not survived.
   *
   * This is the only place in the game where a mayor's money changes what happens
   * to a colony, and it is two lines with nothing in them about a colony.
   */
  private isRunOver(gridX: number, gridY: number): boolean {
    if (this.deps.isSheltered?.(gridX, gridY)) return false;
    return this.isCarAtCell(gridX, gridY);
  }

  private isCarAtCell(gridX: number, gridY: number): boolean {
    const cars = this.deps.cars.getCars();
    for (const car of cars) {
      if (car.state === 'despawning') continue;
      if (car.state === 'parked') continue;

      const carCurrentX = Math.round(car.gridX + (car.targetGridX - car.gridX) * car.progress);
      const carCurrentY = Math.round(car.gridY + (car.targetGridY - car.gridY) * car.progress);

      if ((car.gridX === gridX && car.gridY === gridY)
        || (car.targetGridX === gridX && car.targetGridY === gridY)
        || (carCurrentX === gridX && carCurrentY === gridY)) {
        return true;
      }
    }
    return false;
  }

  private isHouseAtCell(gridX: number, gridY: number): boolean {
    const houses = this.deps.houses.getHouses();
    for (const house of houses) {
      for (const cell of house.footprint) {
        if (house.gridX + cell.dx === gridX && house.gridY + cell.dy === gridY) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * @param cause what killed it. Two things can: a car, and the sun — and the sun
   *   twice over, because an ant that could still have gone home and an ant whose
   *   hole is a foundation are the same corpse and not the same death.
   */
  private killSurfaceAnt(ant: SurfaceAnt, cause: 'car collision' | 'exposure' | 'sealed out'): void {
    ant.state = 'dead';
    ant.corpseTimer = CORPSE_LINGER_MS;

    this.deps.pheromones.depositAlarm(ant.gridX, ant.gridY);

    this.deps.events.emit(GameEventTypes.ANT_KILLED_ON_SURFACE, {
      antId: ant.id,
      name: ant.name,
      gridX: ant.gridX,
      gridY: ant.gridY,
      cause,
      colonyIndex: ant.colonyIndex,
    }, this.deps.layerId, `formicarium-${ant.colonyIndex}`);
  }

  private tryMoveAnt(ant: SurfaceAnt): void {
    const sensors = this.deps.pheromones.sampleSensors(ant.gridX, ant.gridY, ant.heading);
    const antPortalCell = this.getPortalCell(ant.colonyIndex);
    const eps = 0.001;

    // --- Trail confidence update (continuous, proportional to signal) ---
    const maxTrail = Math.max(sensors.tF, sensors.tFL, sensors.tFR);
    if (maxTrail > this.deps.trailFollowThreshold) {
      // Gain proportional to signal strength, not binary
      ant.trailConfidence = Math.min(1, ant.trailConfidence + this.deps.pheromones.TRAIL_CONFIDENCE_GAIN * maxTrail);
      ant.trailSenseCounter++;
    } else {
      ant.trailConfidence = Math.max(0, ant.trailConfidence - this.deps.pheromones.TRAIL_CONFIDENCE_DECAY);
      ant.trailSenseCounter = 0;
    }

    // --- Wall follow timer ---
    if (ant.wallFollowTimer > 0) {
      ant.wallFollowTimer -= ant.speed;
    } else {
      ant.wallFollowSide = 'none';
    }

    // --- Unified weighted steering blend ---
    let turnIntent = 0;
    const isForaging = ant.state === 'foraging';
    const isReturning = ant.state === 'returning';
    const tc = ant.trailConfidence;

    // Compute gradient signals
    const trailGradient = (sensors.tFL - sensors.tFR) / (sensors.tFL + sensors.tFR + eps);
    const foodGradient = (sensors.foodFL - sensors.foodFR) / (sensors.foodFL + sensors.foodFR + eps);
    const nestGradient = (sensors.nestFL - sensors.nestFR) / (sensors.nestFL + sensors.nestFR + eps);
    const alarmGradient = (sensors.aFL - sensors.aFR) / (sensors.aFL + sensors.aFR + eps);
    const maxAlarm = Math.max(sensors.aF, sensors.aFL, sensors.aFR);

    // Direct homing signal (angle-based, for returning ants)
    let directHomingSignal = 0;
    let distToPortal = Infinity;
    if (antPortalCell) {
      const dx = antPortalCell.x - ant.gridX;
      const dy = antPortalCell.y - ant.gridY;
      distToPortal = Math.abs(dx) + Math.abs(dy);
      if (distToPortal > 0) {
        const targetAngle = Math.atan2(dy, dx);
        const headingAngle = ((ant.heading - 2) * Math.PI) / 4;
        let angleDiff = targetAngle - headingAngle;
        while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
        while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
        directHomingSignal = angleDiff;
      }
    }

    // Edge bias signal (thigmotaxis: mild preference for walking along obstacles)
    const edgeBias = isForaging ? this.computeEdgeBias(ant) : 0;

    // Noise (increases under strong alarm - ants become erratic)
    const noiseAmount = this.getTerrainNoise(ant.gridX, ant.gridY);
    const alarmNoiseBoost = maxAlarm > 0.3 ? 1.5 : 1.0;
    const noise = (this.deps.rng.next() - 0.5) * noiseAmount * 2 * alarmNoiseBoost;

    // Very strong alarm: freeze response (skip one move tick). Both draws are
    // taken unconditionally: every move decision costs the same three values
    // whatever the ant is standing in.
    const freezeRoll = this.deps.rng.chance(0.3);
    const alarmJitter = this.deps.rng.next() - 0.5;
    if (maxAlarm > 0.6 && freezeRoll) {
      return; // Ant freezes, skips this move
    }

    const bw = this.deps.blendWeights;

    if (ant.behaviorMode === 'wall_follow' && ant.wallFollowSide !== 'none') {
      // Wall-following overrides the blend temporarily
      turnIntent = this.computeWallFollowTurn(ant);
    } else if (isForaging) {
      // Foraging weights - trail/food balance shifts with trail confidence
      const wTrail = (0.1 + 0.5 * tc) * (bw.trailWeight / 0.5); // Normalize around default
      const wFood = bw.foodOdorWeight * (1 - tc);
      const wEdge = bw.edgeWeight;
      const wAlarm = bw.alarmWeight;
      const wNoise = bw.noiseWeight * (1 - tc);

      turnIntent = wTrail * trailGradient
                 + wFood * foodGradient
                 + wEdge * edgeBias
                 + wNoise * noise;

      // Alarm avoidance (turn away from alarm gradient)
      if (maxAlarm > 0.1) {
        turnIntent -= wAlarm * alarmGradient;
        // Strong alarm: additional turn-away bias. With no gradient to follow,
        // the ant picks a side.
        if (maxAlarm > 0.3) {
          turnIntent -= 0.3 * Math.sign(alarmGradient || alarmJitter);
        }
      }
    } else if (isReturning) {
      // Returning weights - direct homing strengthens near portal
      const wTrail = 0.15;
      const wNest = bw.nestWeight;
      const wDirect = bw.directWeight + 0.20 / (1 + distToPortal * 0.2);
      const wAlarm = bw.alarmWeight * 0.67;
      const wNoise = 0.10;

      turnIntent = wTrail * trailGradient
                 + wNest * nestGradient
                 + wDirect * directHomingSignal
                 + wNoise * noise;

      if (maxAlarm > 0.1) {
        turnIntent -= wAlarm * alarmGradient;
      }
    }

    // --- Apply turn ---
    const turnThreshold = 0.3;
    if (turnIntent > turnThreshold) {
      ant.heading = turnDirection(ant.heading, 1);
    } else if (turnIntent < -turnThreshold) {
      ant.heading = turnDirection(ant.heading, -1);
    }

    const dirVector = DIR_VECTORS[ant.heading];
    let newX = ant.gridX + dirVector.dx;
    let newY = ant.gridY + dirVector.dy;

    // --- Wall collision: enter wall-follow mode ---
    const wallSideRoll = this.deps.rng.chance(0.5);
    if (!this.isValidMoveTarget(newX, newY)) {
      ant.wallFollowTimer = this.deps.wallFollowDuration;
      ant.wallFollowSide = ant.wallFollowSide === 'none'
        ? (wallSideRoll ? 'left' : 'right')
        : ant.wallFollowSide;
      ant.behaviorMode = 'wall_follow';

      const altDir = this.findAlternateDirection(ant);
      if (altDir !== null) {
        ant.heading = altDir;
        const altVector = DIR_VECTORS[altDir];
        newX = ant.gridX + altVector.dx;
        newY = ant.gridY + altVector.dy;
      } else {
        ant.stuckCounter += 2;
        return;
      }
    }

    // --- Stuck counter for returning ants ---
    if (antPortalCell) {
      const currentDistance = Math.abs(antPortalCell.x - ant.gridX) + Math.abs(antPortalCell.y - ant.gridY);
      const newDistance = Math.abs(antPortalCell.x - newX) + Math.abs(antPortalCell.y - newY);
      if (isReturning && newDistance >= currentDistance) {
        ant.stuckCounter++;
      } else {
        ant.stuckCounter = Math.max(0, ant.stuckCounter - 1);
      }
    }

    // --- Update diagnostic behavior mode label ---
    this.updateBehaviorLabel(ant, maxTrail);

    const cellKey = `${ant.gridX},${ant.gridY}`;
    ant.recentCells.push(cellKey);
    if (ant.recentCells.length > 12) {
      ant.recentCells.shift();
    }

    ant.targetGridX = newX;
    ant.targetGridY = newY;
    ant.progress = 0;
  }

  /**
   * Diagnostic-only: label the current behavior mode for debugging/display.
   */
  private updateBehaviorLabel(ant: SurfaceAnt, maxTrail: number): void {
    if (ant.wallFollowTimer > 0) {
      ant.behaviorMode = 'wall_follow';
    } else if (ant.trailConfidence > 0.5 && maxTrail > this.deps.trailFollowThreshold) {
      ant.behaviorMode = 'follow_trail';
    } else if (ant.trailConfidence > 0.1) {
      ant.behaviorMode = 'local_search';
    } else {
      ant.behaviorMode = 'search';
    }
  }

  private computeWallFollowTurn(ant: SurfaceAnt): number {
    const sideDir = ant.wallFollowSide === 'left' ? -2 : 2;
    const sideHeading = turnDirection(ant.heading, sideDir);
    const sideVector = DIR_VECTORS[sideHeading];
    const sideX = ant.gridX + sideVector.dx;
    const sideY = ant.gridY + sideVector.dy;

    if (this.isValidMoveTarget(sideX, sideY)) {
      return ant.wallFollowSide === 'left' ? -0.8 : 0.8;
    }

    return 0;
  }

  /**
   * Compute edge bias (thigmotaxis): mild preference for walking along obstacles.
   */
  private computeEdgeBias(ant: SurfaceAnt): number {
    // Check left and right 90-degree neighbors for obstacles
    const leftDir = turnDirection(ant.heading, -2); // 90° left
    const rightDir = turnDirection(ant.heading, 2); // 90° right
    const leftVec = DIR_VECTORS[leftDir];
    const rightVec = DIR_VECTORS[rightDir];

    const wallLeft = !this.isValidMoveTarget(ant.gridX + leftVec.dx, ant.gridY + leftVec.dy);
    const wallRight = !this.isValidMoveTarget(ant.gridX + rightVec.dx, ant.gridY + rightVec.dy);

    // If wall on left, bias toward left (stay near wall); if on right, bias right
    if (wallLeft && !wallRight) return -0.15;
    if (wallRight && !wallLeft) return 0.15;
    return 0;
  }

  private getTerrainNoise(x: number, y: number): number {
    const cell = this.deps.grid.getCell(x, y);
    if (!cell) return 0.3;

    if (cell.state === CellState.GRADED) return 0.5;
    if (cell.state === CellState.PAINTED || cell.state === CellState.CURED) return 0.15;
    return 0.3;
  }

  private findAlternateDirection(ant: SurfaceAnt): Direction8 | null {
    const turnOrder = ant.wallFollowSide === 'left'
      ? [-1, -2, 1, -3, 2, -4, 3, 4]
      : [1, 2, -1, 3, -2, 4, -3, -4];

    for (const turn of turnOrder) {
      const testDir = turnDirection(ant.heading, turn);
      const vec = DIR_VECTORS[testDir];
      const testX = ant.gridX + vec.dx;
      const testY = ant.gridY + vec.dy;

      if (this.isValidMoveTarget(testX, testY)) {
        const cellKey = `${testX},${testY}`;
        if (!ant.recentCells.includes(cellKey) || ant.stuckCounter > 5) {
          return testDir;
        }
      }
    }

    for (const turn of turnOrder) {
      const testDir = turnDirection(ant.heading, turn);
      const vec = DIR_VECTORS[testDir];
      const testX = ant.gridX + vec.dx;
      const testY = ant.gridY + vec.dy;

      if (this.isValidMoveTarget(testX, testY)) {
        return testDir;
      }
    }

    return null;
  }

  private isValidMoveTarget(x: number, y: number): boolean {
    if (x < 0 || x >= this.deps.grid.getCols() || y < 0 || y >= this.deps.grid.getRows()) {
      return false;
    }

    if (this.isHouseAtCell(x, y)) {
      return false;
    }

    return true;
  }

  private getPortalCell(colonyIndex: number): { x: number; y: number } | undefined {
    return this.deps.portalCells[colonyIndex] || this.deps.portalCells[0];
  }

  /**
   * Whether this colony's entrance is under a federal foundation.
   *
   * `SEALED` and nothing else, which is the whole of the ruling (Q27). The mayor's
   * pavement does **not** close a hole: an ant coming up through the crack in a
   * parking lot is `DESIGN.md`'s founding image and a colony living in the margins
   * of somebody's development is the thesis. `CellState`'s own comment already
   * drew the line this predicate reads — a facility's slab *"is not one of the
   * paved states, though it is concrete and it is permanent"* — so the seal is one
   * comparison against a cell somebody poured for a different reason.
   *
   * Derived, never stored. Nothing un-seals a cell (`setCellState` reaches
   * `SEALED` from exactly one line, in `FacilityManager.place`, and no line
   * anywhere leaves it), so "permanently closed" needs no flag to be permanent —
   * and a district reloads with its entrances exactly as closed as it left them,
   * because the ground came back with the grid. No snapshot field, no hash key,
   * no version.
   */
  entranceIsSealed(colonyIndex: number): boolean {
    const cell = this.getPortalCell(colonyIndex);
    if (!cell) return false;
    return this.deps.grid.getCell(cell.x, cell.y)?.state === CellState.SEALED;
  }
}
